/**
 * Scene-strip posters: a small render of a scene at a chosen time, so the
 * timeline shows the frame the user picked rather than the static page thumb.
 *
 * The result is a data URL kept in component state only. `VideoScene.thumbAtMs`
 * is what gets saved — a data URL in the project document would blow the
 * 1 MB Firestore limit. The poster is rendered again from that timestamp.
 */

import type { ProjectPage, VideoScene } from '@canvas/shared-types';
import { createSceneRenderer } from './sceneCompositor';
import { scenePosterMs } from './sceneTimeline';

const POSTER_MAX_SIDE = 160;

export async function renderSceneThumbToDataUrl(args: {
  page: ProjectPage;
  scene: VideoScene;
  /** Defaults to the scene's saved poster time. */
  atMs?: number;
  maxSide?: number;
}): Promise<string> {
  const data = args.page.canvasData;
  const aw = data?.width || 1200;
  const ah = data?.height || 800;
  const scale = (args.maxSide ?? POSTER_MAX_SIDE) / Math.max(aw, ah, 1);
  const out = {
    width: Math.max(2, Math.round(aw * scale)),
    height: Math.max(2, Math.round(ah * scale)),
  };
  const renderer = await createSceneRenderer({ page: args.page, scene: args.scene, out });
  try {
    const canvas = await renderer.renderAt(args.atMs ?? scenePosterMs(args.scene));
    return canvas.toDataURL('image/jpeg', 0.72);
  } finally {
    renderer.dispose();
  }
}
